import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAppState } from '../../contexts/AppStateContext';
import FileUpload from '../common/FileUpload';
import ToolHeader from '../common/ToolHeader';
import { loadPdfDocument, convertPdfPagesToImages } from '../../utils/pdfUtils';
import { formatFileSize, parsePageRange, downloadFile } from '../../utils/fileUtils';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiDownload, FiLoader } = FiIcons;

const PdfToImageTool = () => {
  const { t } = useLanguage();
  const { state, dispatch } = useAppState();
  const [currentPagePreview, setCurrentPagePreview] = useState(1);

  useEffect(() => {
    if (state.selectedFile && state.selectedFile.type === 'application/pdf') {
      loadPdf();
    }
  }, [state.selectedFile]);

  const loadPdf = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const pdf = await loadPdfDocument(state.selectedFile);
      dispatch({ 
        type: 'SET_PDF_DOCUMENT', 
        payload: { pdf, numPages: pdf.numPages }
      });
      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      console.error('PDF 加載錯誤:', error);
      dispatch({ type: 'SET_ERROR', payload: t('error') });
      if (window.showToast) {
        window.showToast(t('error'), 'error');
      }
    }
  };

  const handleFileUpload = (file) => {
    if (file.type !== 'application/pdf') {
      if (window.showToast) {
        window.showToast(t('invalidFileType'), 'error');
      }
      return;
    }
    dispatch({ type: 'SET_SELECTED_FILE', payload: file });
  };

  const handleConvert = async () => {
    if (!state.pdfDocument) {
      if (window.showToast) {
        window.showToast(t('pleaseUploadFile'), 'warning');
      }
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      let pageNumbers = [];
      if (state.pageRange === 'all') {
        pageNumbers = Array.from({ length: state.totalPages }, (_, i) => i + 1);
      } else if (state.pageRange === 'current') {
        pageNumbers = [currentPagePreview];
      } else if (state.pageRange === 'custom' && state.customPageRange) {
        pageNumbers = parsePageRange(state.customPageRange, state.totalPages);
      }

      if (pageNumbers.length === 0) {
        if (window.showToast) {
          window.showToast(t('pleaseUploadFile'), 'warning');
        }
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }

      const results = await convertPdfPagesToImages(
        state.pdfDocument,
        pageNumbers,
        state.imageFormat,
        state.imageQuality / 100,
        (current, total, pageNum) => {
          // 進度更新
          console.log(`處理進度: ${current}/${total}, 當前頁: ${pageNum}`);
        }
      );

      dispatch({ type: 'SET_CONVERTED_PAGES', payload: results });
      
      if (window.showToast) {
        window.showToast(t('conversionSuccess'), 'success');
      }
    } catch (error) {
      console.error('轉換錯誤:', error);
      dispatch({ type: 'SET_ERROR', payload: t('error') });
      if (window.showToast) {
        window.showToast(t('error'), 'error');
      }
    }
  };

  const handleDownloadAll = () => {
    const extension = state.imageFormat === 'image/jpeg' ? 'jpg' : 'png';
    
    state.convertedPages.forEach((page, index) => {
      setTimeout(() => {
        downloadFile(page.blob, `page-${page.pageNumber}.${extension}`);
      }, index * 200);
    });
  };

  const handleDownloadSingle = (page) => {
    const extension = state.imageFormat === 'image/jpeg' ? 'jpg' : 'png';
    downloadFile(page.blob, `page-${page.pageNumber}.${extension}`);
  };

  if (state.showResults && state.convertedPages.length > 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <ToolHeader title={t('conversionResults')} />
        
        <div className="p-6">
          <div className="mb-6 flex justify-between items-center">
            <h4 className="text-lg font-semibold text-gray-800">
              {t('conversionSuccess')} ({state.convertedPages.length} {t('page')})
            </h4>
            <motion.button
              className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleDownloadAll}
            >
              <SafeIcon icon={FiDownload} />
              {t('downloadAll')}
            </motion.button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {state.convertedPages.map((page) => (
              <motion.div
                key={page.pageNumber}
                className="border rounded-lg p-4 bg-gray-50"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <img
                  src={page.url}
                  alt={`Page ${page.pageNumber}`}
                  className="w-full h-48 object-contain mb-3 bg-white rounded"
                />
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    {t('page')} {page.pageNumber}
                  </span>
                  <motion.button
                    className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleDownloadSingle(page)}
                  >
                    {t('downloadImage')}
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-6 text-center">
            <motion.button
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => dispatch({ type: 'RESET_TOOL' })}
            >
              {t('startOver')}
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <ToolHeader 
        title={t('pdfToImage')}
        filename={state.selectedFile?.name}
      />
      
      <div className="p-6">
        <p className="text-gray-600 mb-6">
          {t('pdfToImageDesc')}
        </p>
        
        {!state.showSettings ? (
          <FileUpload
            onFileUpload={handleFileUpload}
            acceptedTypes="application/pdf"
            uploadText={t('dropOrClickPdf')}
            supportText={t('supportPdf')}
          />
        ) : (
          <div className="space-y-6">
            {state.isLoading && !state.pdfDocument ? (
              <div className="text-center py-12">
                <SafeIcon icon={FiLoader} className="w-12 h-12 text-purple-600 mx-auto mb-4 animate-spin" />
                <p className="text-gray-600">{t('loadingPdf')}</p>
              </div>
            ) : state.pdfDocument ? (
              <>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <span className="text-sm text-gray-600">{t('fileSize')}:</span>
                      <span className="ml-2 font-medium">{formatFileSize(state.originalSize)}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">{t('pageCount')}:</span>
                      <span className="ml-2 font-medium">{state.totalPages}</span>
                    </div>
                  </div>

                  <h4 className="font-medium text-gray-800 mb-4">
                    {t('conversionSettings')}
                  </h4>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('pageRange')}
                      </label>
                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="pageRange"
                            value="all"
                            checked={state.pageRange === 'all'}
                            onChange={(e) => dispatch({ 
                              type: 'UPDATE_SETTINGS', 
                              payload: { pageRange: e.target.value }
                            })}
                            className="mr-2"
                          />
                          {t('allPages')}
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="pageRange"
                            value="current"
                            checked={state.pageRange === 'current'}
                            onChange={(e) => dispatch({ 
                              type: 'UPDATE_SETTINGS', 
                              payload: { pageRange: e.target.value }
                            })}
                            className="mr-2"
                          />
                          {t('currentPage')}
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="pageRange"
                            value="custom"
                            checked={state.pageRange === 'custom'}
                            onChange={(e) => dispatch({ 
                              type: 'UPDATE_SETTINGS', 
                              payload: { pageRange: e.target.value }
                            })}
                            className="mr-2"
                          />
                          {t('custom')}
                        </label>
                      </div>
                      {state.pageRange === 'custom' && (
                        <input
                          type="text"
                          placeholder="1-3, 5, 8-10"
                          value={state.customPageRange}
                          onChange={(e) => dispatch({ 
                            type: 'UPDATE_SETTINGS', 
                            payload: { customPageRange: e.target.value }
                          })}
                          className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('outputFormat')}
                        </label>
                        <select 
                          value={state.imageFormat}
                          onChange={(e) => dispatch({ 
                            type: 'UPDATE_SETTINGS', 
                            payload: { imageFormat: e.target.value }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="image/jpeg">JPG</option>
                          <option value="image/png">PNG</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('imageQuality')}
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="range"
                            min="1"
                            max="100"
                            value={state.imageQuality}
                            onChange={(e) => dispatch({ 
                              type: 'UPDATE_SETTINGS', 
                              payload: { imageQuality: parseInt(e.target.value) }
                            })}
                            className="flex-1"
                          />
                          <span className="text-sm text-gray-600 w-12">{state.imageQuality}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 justify-center">
                  <motion.button
                    className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    whileHover={{ scale: state.isLoading ? 1 : 1.02 }}
                    whileTap={{ scale: state.isLoading ? 1 : 0.98 }}
                    onClick={handleConvert}
                    disabled={state.isLoading}
                  >
                    {state.isLoading ? (
                      <>
                        <SafeIcon icon={FiLoader} className="animate-spin" />
                        {t('convertingPleaseWait')}
                      </>
                    ) : (
                      t('startConversion')
                    )}
                  </motion.button>
                  
                  <motion.button
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => dispatch({ type: 'RESET_TOOL' })}
                  >
                    {t('reuploadFile')}
                  </motion.button>
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default PdfToImageTool;